/**
 * De tweede factor instellen.
 *
 * De routes bestonden sinds stage 1 en werkten; er was alleen nooit een scherm, dus
 * geen enkele verhuurder kon zijn tweede factor aanzetten (§17.48). Dat is hetzelfde
 * patroon als bij het wachtwoord — regel 11 — en de vierde keer dat het in dit project
 * toeslaat.
 *
 * Twee dingen die dit scherm nadrukkelijk doet:
 *
 *   - **De herstelcodes krijgen ruimte en een waarschuwing.** Ze komen precies één
 *     keer over de lijn; daarna staat alleen de argon2-hash in de database en kan
 *     niemand ze teruggeven, wij ook niet. Wie ze wegklikt zonder te lezen, is bij
 *     verlies van zijn telefoon zijn account kwijt.
 *   - **De inschrijving telt pas na bevestiging.** Een geheim dat wel is aangemaakt
 *     maar nooit bevestigd, sluit niemand buiten — precies daarom vraagt dit scherm om
 *     de eerste code voordat het klaar is.
 *
 * Geen QR-code, om dezelfde reden als in de beheeromgeving: een QR-generator is een
 * afhankelijkheid erbij, elke authenticator-app accepteert handmatige invoer, en op de
 * telefoon zelf werkt de `otpauth:`-link.
 */
import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import { authErrorMessageKey, confirmMfa, enrollMfa } from '../lib/auth-client'
import { useTranslate } from '../lib/i18n-provider'

interface Enrollment {
  readonly secret: string
  readonly otpauthUrl: string
  readonly recoveryCodes: readonly string[]
}

export function MfaEnrollment({ onConfirmed }: { onConfirmed: () => void }): ReactElement {
  const t = useTranslate()
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function start(): Promise<void> {
    setBusy(true)
    setError(null)
    const result = await enrollMfa()
    setBusy(false)
    if (!result.ok) {
      setError(t(authErrorMessageKey(result.error, 'mfa')))
      return
    }
    setEnrollment(result.data)
  }

  async function confirm(event: FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const result = await confirmMfa(code.trim())
    setBusy(false)
    if (!result.ok) {
      setError(t(authErrorMessageKey(result.error, 'mfa')))
      return
    }
    onConfirmed()
  }

  return (
    <div className="account__panel">
      <h2>{t('dashboard.account.mfa.title')}</h2>

      {error !== null ? (
        <p className="account__error" role="alert">
          {error}
        </p>
      ) : null}

      {enrollment === null ? (
        <>
          <p className="account__hint">{t('dashboard.account.mfa.why')}</p>
          <button
            className="button"
            type="button"
            disabled={busy}
            onClick={() => {
              void start()
            }}
          >
            {busy ? t('dashboard.account.mfa.starting') : t('dashboard.account.mfa.setUp')}
          </button>
        </>
      ) : (
        <>
          <p className="account__hint">{t('dashboard.account.mfa.scanHint')}</p>
          <p>
            <code className="account__secret">{enrollment.secret}</code>
          </p>
          <p className="account__hint">
            <a href={enrollment.otpauthUrl}>{t('dashboard.account.mfa.openInApp')}</a>
          </p>

          <p className="account__warning">{t('dashboard.account.mfa.recoveryWarning')}</p>
          <ul className="account__codes">
            {enrollment.recoveryCodes.map((recovery) => (
              <li key={recovery}>{recovery}</li>
            ))}
          </ul>

          <form
            onSubmit={(event) => {
              void confirm(event)
            }}
          >
            <label>
              <span>{t('dashboard.account.mfa.code')}</span>
              <input
                autoComplete="one-time-code"
                inputMode="numeric"
                value={code}
                required
                onChange={(event) => {
                  setCode(event.target.value)
                }}
              />
            </label>
            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? t('dashboard.account.mfa.confirming') : t('dashboard.account.mfa.confirm')}
            </button>
          </form>
        </>
      )}
    </div>
  )
}
